import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { decodeJWT } from '../../../utils/jwt';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import styled from 'styled-components';
import { toast as toastify, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import googleIcon from '../../../assets/icons/google-icon.svg';
import facebookIcon from '../../../assets/icons/facebook-icon.svg';
import appleIcon from '../../../assets/icons/apple-icon.svg';

// Util simples para carregar o script do Google Identity Services uma vez
let googleScriptPromise = null;
const loadGoogleScript = () => {
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.accounts?.id) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Falha ao carregar Google Identity'));
    document.body.appendChild(script);
  });
  return googleScriptPromise;
};

// Loader do Facebook SDK
let fbScriptPromise = null;
const loadFacebookSDK = (appId) => {
  if (fbScriptPromise) return fbScriptPromise;
  fbScriptPromise = new Promise((resolve, reject) => {
    if (window.FB) return resolve();
    window.fbAsyncInit = function() {
      window.FB.init({ appId, cookie: true, xfbml: false, version: 'v18.0' });
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('Falha ao carregar Facebook SDK'));
    document.body.appendChild(s);
  });
  return fbScriptPromise;
};

const decodeSubFromJwt = (jwt) => {
  try {
    const [, payload] = jwt.split('.');
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const data = JSON.parse(json);
    return data?.sub;
  } catch (_) {
    return null;
  }
};

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  min-height: calc(100vh - 80px);
  background: #f8f9fa;
  padding: 24px 0;
`;

const Container = styled.div`
  width: 100%;
  min-width: 0;
  margin: 0;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 40px;
  font-weight: 500;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: 24px;

  > * {
    min-width: 0;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SocialButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SocialButton = styled.button`
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: ${props => props.$linked ? '#e6fff0' : 'white'};
  border: 2px solid ${props => props.$linked ? '#18a558' : '#e2e8f0'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  color: #333;

  &:hover {
    border-color: #ba824f;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }

  img {
    width: 22px;
    height: 22px;
  }
`;

const Card = styled.div`
  min-width: 0;
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 32px;

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.75rem;
  color: #333;
  margin-bottom: 32px;
  font-weight: 500;
`;

const ProfileImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ImageContainer = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: #8B2E2E;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  margin-bottom: 24px;
`;

const UploadButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: white;
  border: 2px solid #BA824F;
  border-radius: 8px;
  color: #BA824F;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: #BA824F;
    color: white;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ImageNote = styled.p`
  color: #666;
  font-size: 0.875rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${props => props.disabled ? '#f1f5f9' : 'white'};
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #BA824F;
    box-shadow: 0 0 0 3px rgba(186, 130, 79, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const PasswordInput = styled(Input)`
  padding-right: 44px;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;

  &:hover {
    color: #BA824F;
  }
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  text-overflow: ellipsis;
  padding: 12px 16px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%234A5568' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: #BA824F;
    box-shadow: 0 0 0 3px rgba(186, 130, 79, 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: #BA824F;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 24px;

  &:hover {
    background: #a77445;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [profileImage, setProfileImage] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [socialLinks, setSocialLinks] = useState({ google: false, facebook: false, apple: false });
  const [, setGoogleLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    surname: '',
    companyName: '',
    ami: '',
    nif: '',
    licenseNumber: '',
    address: '',
    postalCode: '',
    parish: '',
    prefix: '',
    mainPhone: '',
    commercial: '',
    facebookUrl: '',
    instagramUrl: '',
    websiteUrl: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    taxExemption: 'nao',
    taxExemptionReason: '',
    payment: '',
    currency: '',
    country: ''
  });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [taxExemptions, setTaxExemptions] = useState([]);
  const [taxExemptionsError, setTaxExemptionsError] = useState('');
  const [rawMoeda, setRawMoeda] = useState(undefined);
  useEffect(() => {
    fetch(import.meta.env.VITE_URL_API + '/isencoes-fiscais/ativas')
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { if (!Array.isArray(data)) throw new Error(); setTaxExemptions(data); })
      .catch(() => setTaxExemptionsError('Não foi possível carregar os motivos. Atualize a página ou contacte o suporte.'));
  }, []);

  const sortedCountries = useMemo(() => {
    if (!countries.length) return [];
    const list = [...countries];
    list.sort((a, b) => String(a.nomePAIS || '').localeCompare(String(b.nomePAIS || ''), 'pt', { sensitivity: 'base' }));
    const portugal = list.filter(c => String(c.nomePAIS).toLowerCase() === 'portugal');
    const others = list.filter(c => String(c.nomePAIS).toLowerCase() !== 'portugal');
    return [...portugal, ...others];
  }, [countries]);

  const groupedCurrencies = useMemo(() => {
    if (!currencies.length) return [];
    const eur = currencies.filter(c => String(c.isoMOEDA).toUpperCase() === 'EUR');
    const others = currencies.filter(c => String(c.isoMOEDA).toUpperCase() !== 'EUR');
    const grouped = others
      .sort((a, b) => String(a.tipoMOEDA || '').localeCompare(String(b.tipoMOEDA || ''), 'pt', { sensitivity: 'base' }))
      .reduce((acc, cur) => {
        const label = String(cur.tipoMOEDA || cur.isoMOEDA || '?');
        const first = label.normalize('NFD').replace(/[^\p{L}\p{N}]/gu, '').charAt(0).toUpperCase() || '#';
        const bucket = acc.find(g => g.label === first);
        if (bucket) bucket.items.push(cur); else acc.push({ label: first, items: [cur] });
        return acc;
      }, []);
    grouped.sort((a, b) => a.label.localeCompare(b.label, 'pt', { sensitivity: 'base' }));
    if (eur.length) grouped.unshift({ label: 'EUR', items: eur });
    return grouped;
  }, [currencies]);

  useEffect(() => {
    const fetchCurrencies = async () => { 
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/moedas`, {
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) {
          console.error('Falha ao buscar moedas', res.status, res.statusText);
          return;
        }
        const data = await res.json();
        const list = Array.isArray(data?.Registros) ? data.Registros : [];

        // Remove duplicados apenas pela sigla (ISO), mantém o primeiro
        const uniqueMap = new Map();
        list.forEach((cur) => {
          const iso = (cur.isoMOEDA || '').toString().toUpperCase();
          if (!uniqueMap.has(iso)) {
            uniqueMap.set(iso, { ...cur, isoMOEDA: iso || cur.isoMOEDA });
          }
        });
        const uniqueList = Array.from(uniqueMap.values());

        const sorted = [...uniqueList].sort((a, b) => {
          const isoA = (a.isoMOEDA || '').toUpperCase();
          const isoB = (b.isoMOEDA || '').toUpperCase();
          if (isoA === 'EUR') return -1;
          if (isoB === 'EUR') return 1;
          return String(a.tipoMOEDA || '').localeCompare(String(b.tipoMOEDA || ''), 'pt', { sensitivity: 'base' });
        });
        setCurrencies(sorted);
      } catch (err) {
        console.error('Erro ao buscar moedas', err?.message || err);
      }
    };

    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchCountries = async () => { 
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/paises`, {
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) {
          console.error('Falha ao buscar países', res.status, res.statusText);
          return;
        }
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        // dedupe por nome, mantém primeiro
        const unique = [];
        const seen = new Set();
        list.forEach(item => {
          const name = String(item.nomePAIS || '').trim();
          if (name && !seen.has(name.toLowerCase())) {
            seen.add(name.toLowerCase());
            unique.push(item);
          }
        });
        setCountries(unique);

      } catch (err) {
        console.error('Erro ao buscar países', err?.message || err);
      }
    };

    fetchCountries();
  }, []);

  // Busca o ID do usuário do JWT
  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const payload = decodeJWT(token);
        const id = payload?.codigo || payload?.codigoUSUARIO || payload?.id;
        setUserId(id);
      }
    } catch (err) {
      console.error('Erro ao decodificar token:', err);
    }
  }, []);

  // Busca dados do usuário da API
  const fetchUserProfile = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_URL_API}/usuarios/${userId}`, {
        headers: {
          accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Dados recebidos da API:', data); // Debug
        
        // Carrega foto do perfil se existir
        if (data.fotoUSUARIO) {
          setProfileImage(data.fotoUSUARIO);
        }

        // Status de vinculação social
        setSocialLinks({
          google: Boolean(data.googleidUSUARIO),
          facebook: Boolean(data.facebookidUSUARIO),
          apple: Boolean(data.appleidUSUARIO)
        });
        
        // Mapeia os valores corretamente
        const prefixValue = data.prefixoUSUARIO ? `+${data.prefixoUSUARIO}` : '';
        const commercialValue = data.designacaocomercialUSUARIO === 'Sim' || data.designacaocomercialUSUARIO === 'sim' || data.designacaocomercialUSUARIO === 1 ? 'sim' : 'nao';
        const taxExemptionValue = Number(data.isencaoUSUARIO) > 0 ? 'sim' : 'nao';
        
        // Mapeia pagamento (pode vir como número ou string)
        let paymentValue = '';
        if (data.pagamentoUSUARIO === 1 || data.pagamentoUSUARIO === 'cartao' || data.pagamentoUSUARIO === 'Cartão') {
          paymentValue = 'cartao';
        } else if (data.pagamentoUSUARIO === 2 || data.pagamentoUSUARIO === 'transferencia' || data.pagamentoUSUARIO === 'Transferência') {
          paymentValue = 'transferencia';
        }
        
        // Moeda: guarda o valor bruto; o ISO é resolvido via lista de /moedas
        setRawMoeda(data.moedaUSUARIO ?? null);
        
        const countryValue = data.paisUSUARIO ? String(data.paisUSUARIO) : '';

        setFormData(prev => ({
          ...prev,
          type: data.tipoUSUARIO === 1 ? 'Particular' : data.tipoUSUARIO === 2 ? 'Agente' : 'Imobiliária',
          name: data.nomeUSUARIO || '',
          surname: data.sobrenomeUSUARIO || '',
          companyName: data.nomeempresaUSUARIO || '',
          ami: data.amiUSUARIO || '',
          nif: data.nifUSUARIO || '',
          licenseNumber: data.numerolicencaUSUARIO || '',
          address: data.ruaenumeroUSUARIO || '',
          postalCode: data.codigopostalUSUARIO || '',
          parish: data.freguesiaUSUARIO || '',
          prefix: prefixValue,
          mainPhone: data.numeroprincipalUSUARIO || '',
          commercial: commercialValue,
          facebookUrl: data.linkfacebookUSUARIO || '',
          instagramUrl: data.linkinstagramUSUARIO || '',
          websiteUrl: data.siteUSUARIO || '',
          taxExemption: taxExemptionValue,
          taxExemptionReason: Number(data.isencaoUSUARIO) > 0 ? String(data.isencaoUSUARIO) : '',
          payment: paymentValue,
          country: countryValue
        }));

        console.log('Valores mapeados:', {
          pagamento: { original: data.pagamentoUSUARIO, mapeado: paymentValue },
          moeda: { original: data.moedaUSUARIO },
          pais: { original: data.paisUSUARIO, mapeado: countryValue }
        }); // Debug
      }
    } catch (err) {
      console.error('Erro ao buscar perfil:', err);
      toastify.error('Erro ao carregar dados do perfil');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    }
  }, [userId, fetchUserProfile]);

  // Resolve a moeda salva (codigoMOEDA ou ISO) para o ISO do select; EUR se vazio
  useEffect(() => {
    if (rawMoeda === undefined) return;
    const s = String(rawMoeda ?? '').trim();
    let resolved = null;
    if (!s) {
      resolved = 'EUR';
    } else {
      const byCodigo = currencies.find(c => String(c.codigoMOEDA) === s || String(c.id) === s);
      if (byCodigo?.isoMOEDA) resolved = String(byCodigo.isoMOEDA).toUpperCase();
      else if (/^[A-Za-z]{3}$/.test(s)) resolved = s.toUpperCase();
      else if (currencies.length) resolved = ({ 1: 'BRL', 2: 'USD', 3: 'EUR', 4: 'GBP' })[s] || 'EUR';
    }
    if (resolved) {
      setFormData(prev => (prev.currency ? prev : { ...prev, currency: resolved }));
    }
  }, [rawMoeda, currencies]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Atualizar todo o perfil de uma vez
  const handleUpdateProfile = async () => {
    if (!userId) return;
    if (formData.taxExemption === 'sim' && !taxExemptions.some(i => String(i.codigoISENCAO) === formData.taxExemptionReason)) {
      toastify.error('Selecione um motivo de isenção ativo e aplicável à compra.');
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      
      // Cria FormData para multipart/form-data
      const formDataToSend = new FormData();
      console.log('[Profile] userId sendo enviado:', userId);
      formDataToSend.append('codigoUSUARIO', userId);

      formDataToSend.append('nomeUSUARIO', formData.name || '');
      formDataToSend.append('sobrenomeUSUARIO', formData.surname || '');
      formDataToSend.append('nomeempresaUSUARIO', formData.companyName || '');
      formDataToSend.append('amiUSUARIO', formData.ami || '');
      formDataToSend.append('nifUSUARIO', formData.nif || '');
      formDataToSend.append('numerolicencaUSUARIO', formData.licenseNumber || '');
      formDataToSend.append('ruaenumeroUSUARIO', formData.address || '');
      formDataToSend.append('codigopostalUSUARIO', formData.postalCode || '');
      formDataToSend.append('freguesiaUSUARIO', formData.parish || '');
      // Remove o '+' do prefixo antes de enviar
      formDataToSend.append('prefixoUSUARIO', formData.prefix ? formData.prefix.replace('+', '') : '');
      formDataToSend.append('numeroprincipalUSUARIO', formData.mainPhone || '');
      formDataToSend.append('designacaocomercialUSUARIO', formData.commercial === 'sim' ? 'Sim' : 'Não');
      formDataToSend.append('linkfacebookUSUARIO', formData.facebookUrl || '');
      formDataToSend.append('linkinstagramUSUARIO', formData.instagramUrl || '');
      formDataToSend.append('siteUSUARIO', formData.websiteUrl || '');
      formDataToSend.append('isentoIVA', String(formData.taxExemption === 'sim'));
      formDataToSend.append('isencaoUSUARIO', formData.taxExemption === 'sim' ? formData.taxExemptionReason : '0');
      
      // Converte pagamento de volta para número
      let paymentNumber = '';
      if (formData.payment === 'cartao') paymentNumber = 1;
      else if (formData.payment === 'transferencia') paymentNumber = 2;
      formDataToSend.append('pagamentoUSUARIO', paymentNumber);
      
      // Converte moeda (ISO) de volta para o codigoMOEDA da API
      const selectedMoeda = currencies.find(c => String(c.isoMOEDA).toUpperCase() === String(formData.currency).toUpperCase());
      const currencyToNumber = { 'BRL': 1, 'USD': 2, 'EUR': 3, 'GBP': 4 };
      const currencyNumber = selectedMoeda?.codigoMOEDA ?? currencyToNumber[formData.currency] ?? formData.currency;
      formDataToSend.append('moedaUSUARIO', currencyNumber);
      
      formDataToSend.append('paisUSUARIO', formData.country);

      // Se houver imagem de perfil, adiciona ao FormData
      if (profileImageFile) {
        formDataToSend.append('fotoUSUARIO', profileImageFile);
      }

      // Força atualização adicionando timestamp
      formDataToSend.append('lastUpdate', new Date().toISOString());

      // Debug: mostrar todos os campos do FormData
      console.log('[Profile] FormData sendo enviado:');
      for (const [key, value] of formDataToSend.entries()) {
        console.log(`  ${key}:`, value instanceof File ? `File(${value.name})` : value);
      }

      const response = await fetch(`${import.meta.env.VITE_URL_API}/usuarios/alterar`, {
        method: 'PATCH',
        headers: {
          accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
          // NÃO incluir Content-Type, o browser define automaticamente para multipart/form-data
        },
        body: formDataToSend
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.moloni?.status === 'pendente') toastify.warning(result.moloni.mensagem + ' ' + (result.moloni.detalhe || ''));
        else toastify.success('Perfil atualizado com sucesso!');
        fetchUserProfile(); // Recarrega os dados
        scrollToTop();
      } else {
        const error = await response.json();
        console.error('[Profile] Erro 400 detalhes:', error);
        console.error('[Profile] Erro completo:', JSON.stringify(error, null, 2));
        throw new Error(error.message || error.error || error.mensagem || 'Erro ao atualizar');
      }
    } catch (err) {
      console.error('Erro ao atualizar perfil:', err);
      toastify.error(err.message || 'Erro ao atualizar perfil!');
    }
  };

  // Atualizar senha
  const handleUpdatePassword = async () => {
    if (!userId) return;
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      toastify.warning('Preencha todos os campos de senha!');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      toastify.error('As senhas não coincidem!');
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_URL_API}/usuarios/${userId}/senha`, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          senhaAntiga: formData.oldPassword,
          senhaNova: formData.newPassword
        })
      });
      if (response.ok) {
        toastify.success('Senha atualizada com sucesso!');
        scrollToTop();
        setFormData(prev => ({
          ...prev,
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao atualizar senha');
      }
    } catch (err) {
      toastify.error(err.message || 'Erro ao atualizar senha!');
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLinkSocial = async (provider) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toastify.error('Faça login novamente para vincular.');
        return;
      }

      let providerId = '';
      let socialToken = '';

      if (provider === 'google') {
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID_LOGIN;
        if (!clientId) {
          toastify.error('Defina VITE_GOOGLE_CLIENT_ID (ou VITE_GOOGLE_CLIENT_ID_LOGIN) para vincular com Google.');
          return;
        }
        setGoogleLoading(true);
        await loadGoogleScript();
        if (!window.google?.accounts?.id) {
          throw new Error('Google Identity indisponível.');
        }

        socialToken = await new Promise((resolve, reject) => {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (response) => {
              if (response?.credential) return resolve(response.credential);
              reject(new Error('Não foi possível obter o credential do Google.'));
            }
          });
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              reject(new Error('Login Google cancelado ou não exibido.'));
            }
          });
        });
        providerId = decodeSubFromJwt(socialToken) || '';
      } else {
        if (provider === 'facebook') {
          const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
          if (!appId) {
            toastify.error('Defina VITE_FACEBOOK_APP_ID para vincular com Facebook.');
            return;
          }
          if (window.location.protocol !== 'https:') {
            toastify.error('Facebook Login exige página em HTTPS (use domínio https ou túnel).');
            return;
          }
          await loadFacebookSDK(appId);
          await new Promise((resolve, reject) => {
            window.FB.login((response) => {
              if (response.authResponse) {
                socialToken = response.authResponse.accessToken;
                providerId = response.authResponse.userID;
                resolve();
              } else {
                reject(new Error('Login Facebook cancelado ou falhou.'));
              }
            }, { scope: 'email' });
          });
          if (!providerId) return;
        } else {
          // Placeholder para Apple até SDK
          providerId = window.prompt(`Informe o ID do usuário no ${provider === 'facebook' ? 'Facebook' : 'Apple'}`) || '';
          if (!providerId) return;
        }
      }

      const resp = await fetch(`${import.meta.env.VITE_URL_API}/usuarios/social/vincular`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ provider, providerId, token: socialToken })
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText || 'Falha ao vincular');
      }

      setSocialLinks(prev => ({ ...prev, [provider]: true }));
      toastify.success(`Conta ${provider} vinculada com sucesso!`);
      scrollToTop();
    } catch (err) {
      console.error('Erro ao vincular social:', err);
      toastify.error(err.message || 'Erro ao vincular conta social');
    } finally {
      if (provider === 'google') setGoogleLoading(false);
    }
  };

  function handleImageChange(e) {
    const file = e.target.files[0];
    if (file) {
      // Armazena o arquivo para enviar no PATCH
      setProfileImageFile(file);
      
      // Cria preview local
      const reader = new FileReader();
      reader.onload = (ev) => {
        setProfileImage(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  }

  const phonePrefixes = ['+1', '+55', '+351', '+44', '+33', '+49', '+39', '+34', '+52', '+54', '+56', '+57', '+58'];

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p>Carregando perfil...</p>
          </div>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ToastContainer position="top-right" autoClose={3000} />
      <Container>
        <Title>Meu Perfil</Title>
        
        <GridContainer>
          <div>
            <Card>
              <ProfileImageSection>
                <ImageContainer>
                  {profileImage ? (
                    <img src={profileImage} alt="Perfil" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} />
                  ) : (
                    formData.name ? formData.name[0].toUpperCase() : '?'
                  )}
                </ImageContainer>
                <UploadButton as="label">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Carregar Imagem
                  <input type="file" accept="image/*" style={{display:'none'}} onChange={handleImageChange} />
                </UploadButton>
                <Button type="button" onClick={() => {
                  setProfileImage('');
                  setProfileImageFile(null);
                }}>Remover Imagem</Button>
                <ImageNote>*mínimo 500px x 500px</ImageNote>
              </ProfileImageSection>
            </Card>

            <Card>
              <CardTitle>Mudar Senha</CardTitle>
              <InputGroup>
                <Label>Senha Antiga</Label>
                <PasswordWrapper>
                  <PasswordInput type={showOldPassword ? 'text' : 'password'} name="oldPassword" value={formData.oldPassword} onChange={handleChange} />
                  <EyeButton type="button" onClick={() => setShowOldPassword(v => !v)} aria-label={showOldPassword ? 'Ocultar senha' : 'Mostrar senha'} title={showOldPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showOldPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </EyeButton>
                </PasswordWrapper>
              </InputGroup>
              <InputGroup>
                <Label>Nova Senha</Label>
                <PasswordWrapper>
                  <PasswordInput type={showNewPassword ? 'text' : 'password'} name="newPassword" value={formData.newPassword} onChange={handleChange} />
                  <EyeButton type="button" onClick={() => setShowNewPassword(v => !v)} aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'} title={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </EyeButton>
                </PasswordWrapper>
              </InputGroup>
              <InputGroup>
                <Label>Confirmar Nova Senha</Label>
                <PasswordWrapper>
                  <PasswordInput type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} />
                  <EyeButton type="button" onClick={() => setShowConfirmPassword(v => !v)} aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'} title={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </EyeButton>
                </PasswordWrapper>
              </InputGroup>
              <Button type="button" onClick={handleUpdatePassword}>Atualizar Senha</Button>
            </Card>
          </div>

          <div>
            <Card>
              <CardTitle>Meus Dados</CardTitle>
              <FormGrid>
                <InputGroup>
                  <Label>Tipo</Label>
                  <Input type="text" name="type" value={formData.type} disabled />
                </InputGroup>
                <InputGroup>
                  <Label>Nome</Label>
                  <Input type="text" name="name" value={formData.name} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Sobrenome</Label>
                  <Input type="text" name="surname" value={formData.surname} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Nome da Empresa</Label>
                  <Input type="text" name="companyName" value={formData.companyName} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>AMI</Label>
                  <Input type="text" name="ami" value={formData.ami} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>NIF</Label>
                  <Input type="text" name="nif" value={formData.nif} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Número da Licença</Label>
                  <Input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Rua e Número</Label>
                  <Input type="text" name="address" value={formData.address} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Código Postal</Label>
                  <Input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Freguesia</Label>
                  <Input type="text" name="parish" value={formData.parish} onChange={handleChange} />
                </InputGroup>
                <InputGroup>
                  <Label>Prefixo</Label>
                  <Select name="prefix" value={formData.prefix} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    {phonePrefixes.map(prefix => (
                      <option key={prefix} value={prefix}>{prefix}</option>
                    ))}
                  </Select>
                </InputGroup>
                <InputGroup>
                  <Label>Número Principal</Label>
                  <Input type="tel" name="mainPhone" value={formData.mainPhone} onChange={handleChange} />
                </InputGroup>
                <InputGroup className="full-width">
                  <Label>Comercial</Label>
                  <Select name="commercial" value={formData.commercial} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    <option value="sim">Sim</option>
                    <option value="nao">Não</option>
                  </Select>
                </InputGroup>
              </FormGrid>
              <Button type="button" onClick={handleUpdateProfile}>Salvar Dados</Button>
            </Card>

            <Card>
              <CardTitle>Dados da Faturação</CardTitle>
              <FormGrid>
                <InputGroup>
                  <Label htmlFor="taxExemption">IVA na compra da assinatura/anúncio</Label>
                  <Select id="taxExemption" name="taxExemption" value={formData.taxExemption} onChange={e => setFormData(prev => ({ ...prev, taxExemption: e.target.value, taxExemptionReason: '' }))}>
                    <option value="nao">Sem Isenção de IVA</option>
                    <option value="sim">Com Isenção de IVA</option>
                  </Select>
                  <p>O país, por si só, não determina a isenção. A isenção da sua atividade não se aplica automaticamente a esta compra.</p>
                  {formData.taxExemption === 'sim' && <>
                    <Label htmlFor="taxExemptionReason">Código de isenção do IVA</Label>
                    <Select id="taxExemptionReason" name="taxExemptionReason" value={formData.taxExemptionReason} onChange={handleChange} disabled={!taxExemptions.length}>
                      <option value="">Selecione o código de isenção</option>
                      {formData.taxExemptionReason && !taxExemptions.some(i => String(i.codigoISENCAO) === formData.taxExemptionReason) && <option value={formData.taxExemptionReason}>Motivo anterior indisponível — selecione outro</option>}
                      {taxExemptions.map(i => <option key={i.codigoISENCAO} value={String(i.codigoISENCAO)}>{i.descricaoISENCAO || i.nomeISENCAO}</option>)}
                    </Select>
                    <p>Selecione apenas um motivo confirmado com o seu contabilista.</p>
                    {!taxExemptions.length && <p role="alert">{taxExemptionsError || 'Não há motivos disponíveis. Atualize a página ou contacte o suporte.'}</p>}
                  </>}
                </InputGroup>
                <InputGroup>
                  <Label>Pagamento</Label>
                  <Select name="payment" value={formData.payment} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    <option value="cartao">Cartão</option>
                    <option value="transferencia">Transferência</option>
                  </Select>
                </InputGroup>
                <InputGroup>
                  <Label>Moeda</Label>
                  <Select name="currency" value={formData.currency} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    {groupedCurrencies.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.items.map(cur => (
                          <option key={cur.codigoMOEDA || cur.isoMOEDA} value={cur.isoMOEDA}>
                            {cur.isoMOEDA} — {cur.tipoMOEDA}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                 
                </InputGroup>
                <InputGroup>
                  <Label>País</Label>
                  <Select name="country" value={formData.country} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    {sortedCountries.map(c => (
                      <option key={c.codigoPAIS || c.nomePAIS} value={c.codigoPAIS}>
                        {c.nomePAIS}
                      </option>
                    ))}
                  </Select>
                </InputGroup>
              </FormGrid>
              <Button type="button" onClick={handleUpdateProfile}>Salvar Dados de Faturação</Button>
            </Card>

            <Card>
              <CardTitle>Redes Sociais</CardTitle>
              <FormGrid>
                <InputGroup>
                  <Label>Facebook URL</Label>
                  <Input type="text" name="facebookUrl" value={formData.facebookUrl} onChange={handleChange} placeholder="https://facebook.com/..." />
                </InputGroup>
                <InputGroup>
                  <Label>Instagram URL</Label>
                  <Input type="text" name="instagramUrl" value={formData.instagramUrl} onChange={handleChange} placeholder="https://instagram.com/..." />
                </InputGroup>
                <InputGroup className="full-width">
                  <Label>Website URL</Label>
                  <Input type="text" name="websiteUrl" value={formData.websiteUrl} onChange={handleChange} placeholder="https://..." />
                </InputGroup>
              </FormGrid>
              <Button type="button" onClick={handleUpdateProfile}>Salvar Redes Sociais</Button>
            </Card>

            <Card>
              <CardTitle>Login Social (vincular)</CardTitle>
              <p style={{ color: '#555', marginBottom: 16 }}>
                Vincule suas contas para poder entrar com Google, Facebook ou Apple.
              </p>
              <SocialButtons>
                <SocialButton type="button" onClick={() => handleLinkSocial('google')} $linked={socialLinks.google}>
                  <img src={googleIcon} alt="Google" />
                  {socialLinks.google ? 'Google vinculado' : 'Vincular Google'}
                </SocialButton>
                <SocialButton type="button" onClick={() => handleLinkSocial('facebook')} $linked={socialLinks.facebook}>
                  <img src={facebookIcon} alt="Facebook" />
                  {socialLinks.facebook ? 'Facebook vinculado' : 'Vincular Facebook'}
                </SocialButton>
                <SocialButton type="button" onClick={() => handleLinkSocial('apple')} $linked={socialLinks.apple}>
                  <img src={appleIcon} alt="Apple" />
                  {socialLinks.apple ? 'Apple vinculado' : 'Vincular Apple'}
                </SocialButton>
              </SocialButtons>
            </Card>

            {/* Botão único para salvar todo o perfil */}
            <Button 
              type="button" 
              onClick={handleUpdateProfile}
              style={{ 
                fontSize: '1.1rem', 
                padding: '16px',
                background: '#34574A',
                marginTop: 0
              }}
            >
              💾 Salvar Perfil Completo
            </Button>
          </div>
        </GridContainer>
      </Container>
    </PageContainer>
  );
};

export default Profile;
